<script lang="ts">
    import { service } from "@frontend/services";
    import { GetCachedToken, me } from "@frontend/user";
    import { push } from "svelte-spa-router";
    import { onMount } from "svelte";
    import { translate } from "../Translator";
    import type { AuthenticatedRequestOfTransactionbyLimitDTO, TransactionbyLimitResponse, TransactionbyLimitItem, GroupLimitResponse } from "@frontend/walletapi";
    import { groupLimits } from "@frontend/grouplimitlist";

    export let params: { grouplimitid: number } = { grouplimitid: 0 };

    let groupLimit: GroupLimitResponse | null = null;
    let transactionData: TransactionbyLimitResponse | null = null;
    let transactions: TransactionbyLimitItem[] = [];
    let isLoading = true;
    let errorMessage = '';
    let successMessage = '';

    // Función para formatear el dinero
    function formatMoney(amount: any): string {
        if (!amount) return '$0.00';
        const num = typeof amount === 'object' ? amount.InCents / 100 || 0 : Number(amount);
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(num);
    }

    // Función para formatear la fecha
    function formatDate(date: Date | string | undefined): string {
        if (!date) return 'N/A';
        try {
            const dateObj = typeof date === 'string' ? new Date(date) : date;
            return dateObj.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'N/A';
        }
    }

    async function loadTransactions() {
        console.log('🔍 Debug - groupLimit recibido:', groupLimit);
        console.log('🔍 Debug - groupLimit.Id:', groupLimit?.Id);
        console.log('🔍 Debug - groupLimit.GroupId:', groupLimit?.GroupId);
        
        if (!groupLimit || !groupLimit.Id) {
            errorMessage = 'Error: No se encontró el ID del límite de grupo.';
            console.error('❌ Error: groupLimit o groupLimit.Id es undefined/null');
            return;
        }

        isLoading = true;
        errorMessage = '';

        try {
            const requestPayload: AuthenticatedRequestOfTransactionbyLimitDTO = {
                Token: GetCachedToken(),
                Data: {
                    GroupLimitId: groupLimit.Id
                }
            };
            
            console.log('📤 Debug - Request payload:', requestPayload);

            const result = await service.walletapi.transactionbyLimit(requestPayload);

            if (result.Success && result.Data) {
                transactionData = result.Data;
                transactions = result.Data.Transactions || [];
                successMessage = `Se encontraron ${transactions.length} transacciones.`;
            } else {
                errorMessage = result.ErrorMessage || 'No se encontraron transacciones para este límite de grupo.';
                transactionData = null;
                transactions = [];
            }
        } catch (error) {
            errorMessage = 'Error de conexión. Por favor intenta de nuevo.';
            transactionData = null;
            transactions = [];
        } finally {
            isLoading = false;
        }
    }

    function goBack() {
        push("/GroupLimit");
    }

    onMount(() => {
        if (!$me) {
            push("/user/Login");
            return;
        }

        // Buscar el group limit por ID
        const groupLimitId = Number(params.grouplimitid);
        groupLimit = $groupLimits.find(gl => gl.Id === groupLimitId) || null;
        
        if (!groupLimit) {
            errorMessage = `No se encontró el límite de grupo con ID: ${groupLimitId}`;
            isLoading = false;
            return;
        }

        loadTransactions();
    });
</script>

<style>
    .transactions-page-container {
        padding: 1em;
        max-width: 1000px;
        margin: 0 auto;
        background: #f8f9fa;
        min-height: 100vh;
    }
    
    .header-section {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1em;
        padding: 0.8em 1em;
        background: white;
        color: #333;
        border-radius: 6px;
        box-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);
    }

    .header-section h1 {
        color: #333 !important;
        margin: 0;
        font-size: 1.2em;
        display: flex;
        align-items: center;
        gap: 0.4em;
    }

    .header-section .sub.header {
        color: #6c757d !important;
        font-size: 0.8em;
        margin-top: 0.2em;
    }

    .back-button {
        background: #6c757d !important;
        color: white !important;
        border: none !important;
        padding: 0.4em 0.8em !important;
        border-radius: 4px !important;
        font-weight: 600 !important;
        transition: all 0.2s ease !important;
        display: flex;
        align-items: center;
        gap: 0.3em;
        font-size: 0.9em;
    }

    .back-button:hover {
        background: #495057 !important;
    }

    .info-card {
        background: white;
        padding: 0.8em;
        border-radius: 4px;
        margin-bottom: 0.8em;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        border-left: 3px solid #495057;
    }

    .info-card h4 {
        color: #212529;
        margin: 0 0 0.6em 0;
        font-size: 0.9em;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 0.3em;
    }

    .info-card h4 i {
        color: #495057;
    }

    .info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: 0.6em;
        margin-top: 0.6em;
    }

    .info-item {
        display: flex;
        flex-direction: column;
        gap: 0.1em;
    }

    .info-label {
        font-size: 0.7em;
        color: #6c757d;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.2px;
    }

    .info-value {
        font-size: 0.8em;
        color: #212529;
        font-weight: 600;
    }

    .status-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.2em 0.4em;
        border-radius: 3px;
        font-size: 0.7em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.2px;
    }

    .status-active {
        background: #28a745;
        color: white;
    }

    .status-inactive {
        background: #dc3545;
        color: white;
    }

    .summary-card {
        background: #f8f9fa;
        color: #333;
        padding: 0.8em;
        border-radius: 4px;
        margin-bottom: 0.8em;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        border: 1px solid #dee2e6;
    }

    .summary-card h4 {
        color: #333 !important;
        margin: 0 0 0.6em 0;
        font-size: 0.9em;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 0.3em;
    }

    .summary-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
        gap: 0.6em;
    }

    .summary-item {
        text-align: center;
        padding: 0.6em;
        background: rgba(0, 0, 0, 0.05);
        border-radius: 3px;
        border: 1px solid #e9ecef;
    }

    .summary-number {
        font-size: 1em;
        font-weight: 700;
        margin-bottom: 0.1em;
    }

    .summary-label {
        font-size: 0.65em;
        opacity: 0.9;
        text-transform: uppercase;
        letter-spacing: 0.2px;
    }
    
    .transaction-table {
        background: white;
        border-radius: 4px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        margin-bottom: 0.8em;
    }

    .transaction-table table {
        margin: 0;
        border: none;
    }

    .transaction-table thead th {
        background: #f8f9fa;
        color: #495057;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.2px;
        font-size: 0.7em;
        padding: 0.6em;
        border: none;
        border-bottom: 1px solid #dee2e6;
    }

    .transaction-row {
        transition: background-color 0.2s ease;
        border-bottom: 1px solid #f1f3f4;
    }
    
    .transaction-row:hover {
        background: #f8f9fa !important;
    }

    .transaction-row td {
        padding: 0.6em;
        vertical-align: middle;
        border: none;
        font-size: 0.85em;
    }

    .numeric {
        font-family: 'Courier New', monospace;
        font-weight: 600;
        color: #495057;
        font-size: 0.8em;
    }

    .balance-cell {
        text-align: right;
        font-family: 'Courier New', monospace;
        font-weight: 600;
        font-size: 0.8em;
    }
    
    .balance-positive {
        color: #28a745;
        font-weight: 600;
    }
    
    .balance-negative {
        color: #dc3545;
        font-weight: 600;
    }
    
    .balance-zero {
        color: #6c757d;
        font-weight: 600;
    }

    .message {
        border-radius: 4px;
        margin-bottom: 0.8em;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        font-size: 0.9em;
    }

    .ui.negative.message {
        background: #dc3545;
        color: white;
        border: none;
    }

    .ui.positive.message {
        background: #28a745;
        color: white;
        border: none;
    }

    .ui.active.dimmer {
        background: rgba(0, 0, 0, 0.5);
    }

    .ui.text.loader {
        color: white;
        font-weight: 600;
    }

    .placeholder-segment {
        background: white;
        border-radius: 4px;
        padding: 1.5em 1em;
        text-align: center;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .placeholder-segment .ui.icon.header {
        color: #6c757d;
        font-size: 0.9em;
    }

    .placeholder-segment p {
        color: #6c757d;
        margin-top: 0.6em;
        font-size: 0.85em;
    }

    /* Responsive adjustments */
    @media (max-width: 768px) {
        .transactions-page-container {
            padding: 0.8em;
        }

        .header-section {
            padding: 0.8em;
            flex-direction: column;
            gap: 0.6em;
            text-align: center;
        }

        .header-section h1 {
            font-size: 1.1em;
        }

        .info-grid {
            grid-template-columns: 1fr;
            gap: 0.5em;
        }

        .summary-grid {
            grid-template-columns: 1fr;
            gap: 0.5em;
        }

        .transaction-table {
            overflow-x: auto;
        }

        .transaction-table table {
            min-width: 450px;
            font-size: 0.8em;
        }
    }
</style>

<div class="transactions-page-container">
    <div class="header-section">
        <h1 class="ui header">
            <i class="list icon"></i>
            <div class="content">
                {$translate('Transactions')} - {$translate('Limit of Group')}
                <div class="sub header">ID: {groupLimit?.Id}</div>
            </div>
        </h1>
        <button class="ui button back-button" on:click={goBack}>
            <i class="arrow left icon"></i>
            {$translate('Back')}
        </button>
    </div>

    {#if groupLimit}
        <div class="info-card">
            <h4>
                <i class="info circle icon"></i>
                {$translate('Limit information')}
            </h4>
            <div class="info-grid">
                <div class="info-item">
                    <span class="info-label"> {$translate('Group Id ')}</span>
                    <span class="info-value">{groupLimit.GroupId}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">{$translate('User Id')}</span>
                    <span class="info-value">{groupLimit.UserId || 'N/A'}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">Balance Inicial</span>
                    <span class="info-value">{formatMoney(groupLimit.InitialBalance)}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">{$translate('Initial Balance')}</span>
                    <span class="info-value">{formatMoney(groupLimit.Balance)}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">{$translate('State')}</span>
                    <span class="status-badge {groupLimit.Active ? 'status-active' : 'status-inactive'}">
                        {groupLimit.Active ? $translate('Active') : $translate('Inactive')}
                    </span>
                </div>
            </div>
        </div>
    {/if}

    {#if transactionData && transactions.length > 0}
        <div class="summary-card">
            <h4>
                <i class="chart bar icon"></i>
               {$translate('Transaction Summary')}
            </h4>
            <div class="summary-grid">
                <div class="summary-item">
                    <div class="summary-number">{transactions.length}</div>
                    <div class="summary-label">{$translate('Total transactions')}</div>
                </div>
                <div class="summary-item">
                    <div class="summary-number">{transactions[0].LimitTransactionId}</div>
                    <div class="summary-label">{$translate('First Transaction')}</div>
                </div>
                <div class="summary-item">
                    <div class="summary-number">{transactions[transactions.length - 1].LimitTransactionId}</div>
                    <div class="summary-label">{$translate('Last Transaction')}</div>
                </div>
            </div>
        </div>
    {/if}

    {#if errorMessage}
        <div class="ui negative message">
            <div class="header">{$translate('Error')}</div>
            <p>{errorMessage}</p>
        </div>
    {/if}

    {#if successMessage}
        <div class="ui positive message">
            <div class="header">{$translate('Exito')}</div>
            <p>{successMessage}</p>
        </div>
    {/if}

    {#if isLoading}
        <div class="ui active dimmer">
            <div class="ui text loader">{$translate('Loading transactions...')}</div>
        </div>
    {:else if transactions.length > 0}
        <div class="transaction-table">
            <table class="ui celled unstackable striped very compact table">
                <thead>
                    <tr>
                        <th style="width: 60px">#</th>
                        <th style="width: 120px">Id</th>
                        <th style="width: 160px; text-align: right">{$translate('Start Amount')}</th>
                        <th style="width: 160px; text-align: right">{$translate('Balance')}</th>
                        <th style="width: 180px">{$translate('Date Transaction')}</th>
                    </tr>
                </thead>
                <tbody>
                    {#each transactions as transaction, index}
                        <tr class="transaction-row">
                            <td class="numeric">{index + 1}</td>
                            <td class="numeric">{transaction.LimitTransactionId}</td>
                            <td class="balance-cell">
                                <span class="balance-positive">{formatMoney(transaction.InitialBalance)}</span>
                            </td>
                            <td class="balance-cell">
                                {#if transaction.Balance}
                                    {@const balance = transaction.Balance}
                                    {@const balanceAmount = typeof balance === 'object' ? balance.InCents / 100 || 0 : Number(balance)}
                                    <span class={balanceAmount > 0 ? 'balance-positive' : balanceAmount < 0 ? 'balance-negative' : 'balance-zero'}>
                                        {formatMoney(transaction.Balance)}
                                    </span>
                                {:else}
                                    <span class="balance-zero">$0.00</span>
                                {/if}
                            </td>
                            <td>
                                {formatDate(transaction.TransactionTime)}
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    {:else}
        <div class="placeholder-segment">
            <div class="ui icon header">
                <i class="list icon"></i>
                {$translate('no transactions')}
            </div>
            <p>{$translate('No transactions were found for this group limit.')}</p>
        </div>
    {/if}
</div>
